import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, Phone, Mail, Users, Home, HelpCircle, CreditCard, Settings, Wrench } from 'lucide-react';
import MainHeader from '../../Components/Header/MainHeader';
import MainFooter from '../../Components/Footer/MainFooter';

export default function FAQPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const faqCategories = [
    { id: 'all', name: 'All Questions', icon: HelpCircle },
    { id: 'general', name: 'General', icon: Settings },
    { id: 'buying_renting', name: 'Buying And Renting', icon: Home },
    { id: 'fundis', name: 'Hiring A Fundi', icon: Wrench },
    { id: 'listing', name: 'Listing A Property', icon: Users },
    { id: 'payment', name: 'Payment', icon: CreditCard },
  ];

  const faqs = [
    {
      id: 1,
      category: 'general',
      question: 'What is Rekebisha?',
      answer: 'Rekebisha is Kenya\'s marketplace for homes and fundis. Our platform connects buyers and tenants with agents and homeowners, and connects clients with verified handymen, so you can find a home to let or buy and hire the right fundi to work on it, all without middlemen.'
    },
    {
      id: 2,
      category: 'general',
      question: 'Is Rekebisha only for renting, or can I buy property too?',
      answer: 'Both. You can search the app for properties that are to let or for sale, including apartments, bungalows, mansionettes, commercial spaces and luxury homes.'
    },
    {
      id: 3,
      category: 'buying_renting',
      question: 'How do I search for a property?',
      answer: 'Download the Rekebisha app, browse the Properties section, and filter by county, constituency and property type until you find a home that fits your needs.'
    },
    {
      id: 4,
      category: 'buying_renting',
      question: 'How do I contact the agent or homeowner?',
      answer: 'Once you find a listing you like, you can reach out to the agent or homeowner directly through the app. There is no middleman, so you get a direct answer to your questions.'
    },
    {
      id: 5,
      category: 'buying_renting',
      question: 'Are the listings verified?',
      answer: 'Yes. Agents and homeowners on Rekebisha go through a verification process before their listings appear on the platform, so you can browse with confidence.'
    },
    {
      id: 12,
      category: 'fundis',
      question: 'What is a fundi?',
      answer: 'A fundi is a skilled tradesperson or handyman. On Rekebisha you will find plumbers, electricians, carpenters, painters, fumigation experts and household electronics experts, all verified before they appear on the platform.'
    },
    {
      id: 13,
      category: 'fundis',
      question: 'How do I find a fundi near me?',
      answer: 'Go to the Find a Fundi page and filter by profession, county and sub county. Fundis are shown highest rated first, so the most trusted professionals in your area appear at the top.'
    },
    {
      id: 14,
      category: 'fundis',
      question: 'Can I see a fundi\'s previous work before hiring?',
      answer: 'Yes. Click any fundi to open their portfolio, where you can see photos of jobs they have completed, their professional body registration, their competence rating and their contact details.'
    },
    {
      id: 15,
      category: 'fundis',
      question: 'How are fundis rated and verified?',
      answer: 'Every fundi is checked before joining the platform, and each one carries a competence rating and a certification rating built from their credentials and the feedback of clients who have hired them.'
    },
    {
      id: 16,
      category: 'fundis',
      question: 'I am a fundi. How do I join Rekebisha?',
      answer: 'Download the Rekebisha Fundi app from the Play Store, create your profile with your trade and location, and upload photos of your work. Once verified, clients searching your county can find and contact you directly.'
    },
    {
      id: 6,
      category: 'listing',
      question: 'I am an agent. How do I list properties on Rekebisha?',
      answer: 'Create an agent account in the app or through our property manager portal, add your listings with photos and details, and start receiving enquiries directly from buyers and tenants.'
    },
    {
      id: 7,
      category: 'listing',
      question: 'I am a homeowner. Can I list my property myself?',
      answer: 'Yes. Homeowners can list a property for rent or sale without going through an agent, and communicate directly with interested buyers or tenants.'
    },
    {
      id: 8,
      category: 'listing',
      question: 'Is there a fee to list a property?',
      answer: 'Reach out to our team at 0733 212 124 or info@rekebisha.com for current listing terms for agents and homeowners.'
    },
    {
      id: 9,
      category: 'general',
      question: 'What areas does Rekebisha cover?',
      answer: 'We list properties and fundis across Nairobi, Mombasa, Kisumu and many other counties, with coverage expanding as more agents, homeowners and fundis join the platform.'
    },
    {
      id: 10,
      category: 'payment',
      question: 'How do I pay rent or a deposit?',
      answer: 'Payment terms are agreed directly between you and the agent or homeowner. Rekebisha connects you but does not sit between you and your payment arrangement.'
    },
    {
      id: 17,
      category: 'payment',
      question: 'How much does a fundi cost?',
      answer: 'Rates are agreed directly between you and the fundi, since every job is different. We recommend discussing the scope and price before work starts so both sides are clear.'
    },
    {
      id: 11,
      category: 'general',
      question: 'How do I contact customer support?',
      answer: 'You can reach us by calling 0733 212 124, emailing info@rekebisha.com, or through the in-app support option.'
    },
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (faqId) => {
    setOpenFAQ(openFAQ === faqId ? null : faqId);
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <MainHeader />

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center"
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1600&auto=format&fit=crop)` }}
      >
        <div className="absolute inset-0 bg-navy/75"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto mb-8">
            Answers about finding a home, hiring a fundi and listing on Rekebisha.
          </p>

          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search FAQs"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border-0 shadow-lg focus:outline-none focus:ring-4 focus:ring-brandgreen/40 text-lg"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Categories Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
                <h3 className="text-xl font-bold text-navy mb-6">Categories</h3>
                <div className="space-y-3">
                  {faqCategories.map((category) => {
                    const Icon = category.icon;
                    const count = category.id === 'all' ? faqs.length : faqs.filter(faq => faq.category === category.id).length;

                    return (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`w-full text-left p-3 rounded-xl transition-colors duration-300 flex items-center justify-between ${
                          selectedCategory === category.id
                            ? 'bg-navy text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <div className="flex items-center">
                          <Icon className={`w-5 h-5 mr-3 ${selectedCategory === category.id ? 'text-brandgreen' : 'text-gray-500'}`} />
                          <span className="font-medium">{category.name}</span>
                        </div>
                        <span className={`text-sm px-2 py-1 rounded-full ${
                          selectedCategory === category.id
                            ? 'bg-white/20 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* FAQ List */}
            <div className="lg:col-span-3">
              <div className="space-y-4">
                {filteredFAQs.length > 0 ? (
                  filteredFAQs.map((faq) => (
                    <div
                      key={faq.id}
                      className="bg-white rounded-2xl shadow-md overflow-hidden"
                    >
                      <button
                        onClick={() => toggleFAQ(faq.id)}
                        className="w-full p-6 text-left flex items-center justify-between focus:outline-none"
                      >
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-brandgreen/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <HelpCircle className="w-5 h-5 text-brandgreen" />
                          </div>
                          <h3 className="text-lg font-bold text-navy">
                            {faq.question}
                          </h3>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          {openFAQ === faq.id ? (
                            <ChevronUp className="w-6 h-6 text-brandgreen" />
                          ) : (
                            <ChevronDown className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {openFAQ === faq.id && (
                        <div className="px-6 pb-6">
                          <div className="bg-gray-50 rounded-xl p-6 ml-14">
                            <p className="text-gray-700 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-16 bg-white rounded-2xl shadow-md">
                    <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-600 mb-2">No FAQs found</h3>
                    <p className="text-gray-500">Try searching with different keywords or select another category.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Still Have Questions?</h2>
            <p className="text-xl text-gray-300">Our team is here to help you find a home, hire a fundi or list a property</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-brandgreen rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Call Us</h3>
              <p className="text-gray-300 mb-4">Speak directly with our support team</p>
              <a
                href="tel:0733212124"
                className="inline-block bg-brandgreen text-white px-6 py-3 rounded-xl font-bold hover:bg-brandgreen-dark transition-colors duration-300"
              >
                0733 212 124
              </a>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-brandgreen rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Email Us</h3>
              <p className="text-gray-300 mb-4">We reply within 2 hours</p>
              <a
                href="mailto:info@rekebisha.com"
                className="inline-block bg-brandgreen text-white px-6 py-3 rounded-xl font-bold hover:bg-brandgreen-dark transition-colors duration-300"
              >
                info@rekebisha.com
              </a>
            </div>
          </div>
        </div>
      </section>
      <MainFooter />
    </div>
  );
}
